import React from "react"
import { useAlert } from "hooks/useAlert"
import { useTranslation } from "react-i18next"
import * as UserApi from "features/api/UserApi"
import FormElement from "components/Form/FormElement"
import { useAuth } from "hooks/useAuth"
import { BC_OPERATOR } from "utils/constants"


export const ManagerForm = ({
    partner,
    updatedPartner,
    setUpdatedPartner,
    errors,
    setErrors,
    isEdit,
    isViewMode,
    editCallback,
    showLocalErrors,
    setShowLocalErrors,
    refresh
}) => {
    const [formFields, setFormFields] = React.useState({})
    const defaultFormEvents = React.useMemo(() => ({
        ready: true,
        // when form is in edition mode, by default the editable is set at false
        // when the form is not in edition mode, it means creation mode, the form should be editable
        editable:isEdit,
        viewMode: isViewMode || false
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [isEdit])
    const [formEvents, setFormEvents] = React.useState(defaultFormEvents)

    const { alert } = useAlert()
    const { t } = useTranslation()
    const { logout, entity } = useAuth()

    const entityRole = entity?.entity_data?.entity_permission?.role

    React.useEffect(() => {
        // When partner reloaded (after await fetchData()), set data in formFields
        if(partner || !isEdit) setFormFields(partner)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [partner,isEdit])


    React.useEffect(()=>{
        setFormEvents (prev=>({...prev,editable:isEdit}))
    },[isEdit])

    React.useEffect(() => {
        // When we create or update a partner, we have to pass data to parent directly.
        // We don't have any submit button on this component in this case
        setUpdatedPartner((prevFormFields) => ({
            ...prevFormFields,
            nom_representant_legal: formFields.nom_representant_legal,
            prenom_representant_legal: formFields.prenom_representant_legal,
            telephone_1: formFields.telephone_1,
            mail: formFields?.mail?.trim(),
        }))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formFields])

    const handleInputChange = React.useCallback(
        (event) => {
            const target = event.target
            const value = target.type === "checkbox" ? target.checked : target.value
            const name = target.name
            setFormFields(s => ({
                ...s,
                [name]: value
            }))
        }, []
    )

    const handleValidation = React.useCallback((name, errs) => {
        setErrors((s) => {
            const cleanErrors = [...s.filter((e) => e.origin !== name)]
            return [
                ...cleanErrors,
                ...errs
            ]
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])


    const sendMail = async (event) => {
        event.preventDefault()
        // Validate inputs
        const formReady = !(errors.length > 0)

        if (!formReady) {
            setShowLocalErrors(true)
        }
        else {
            try {
                // hide error
                setShowLocalErrors(false)
                // prevent form to be submitted again
                setFormEvents({
                    ...formEvents,
                    ready: false
                })

                let mailtemplate = formFields.data_operators.filter(oneOperator => oneOperator.status === 1)
                mailtemplate = mailtemplate.find(oneOperator => oneOperator.operator === BC_OPERATOR) || mailtemplate[0]

                const manager = {
                    user_id: formFields.manager?.user_id,
                    send_activation_link_by_mail: mailtemplate.operator,
                    row_version: formFields.manager?.row_version
                }

                const { getError } = await UserApi.sendMailToManager(manager)
                if (getError()) throw getError()

                alert("success", t("sendmail_success", { ns: "form" }))

                if(refresh()) refresh()

            } catch (error) {
                console.error(error)
                const erroMsg = error?.message?.toString()
                if (erroMsg) alert("error", erroMsg)

                if (["BikeVersionMismatchException"].includes(error?.code)) if(refresh()) refresh()
                if (["Access token is required."].includes(error?.error?.message)) logout()

            } finally {
                setFormEvents(s => ({
                    ...s,
                    ready: true,
                    editable: false
                }))
            }
        }
    }

    return (
        <section id='manager' className="py-16 px-4 scroll-mt-10">
            <h2 className="c-h2 font-semibold mb-7 uppercase">{t("store.manager", { ns: "partner" })}</h2>
            <div className="l-grid u-mg-negative-hz-m">
                <div className="lg:w-1/2 w-full p-5">
                    {/* contactName */}
                    <FormElement
                        value={formFields.nom_representant_legal}
                        name="nom_representant_legal"
                        type="text"
                        label={t("store.contact_name", { ns: "partner" })}
                        className="mb-5"
                        required={true}
                        disabled={!formEvents.editable}
                        showErrors={showLocalErrors}
                        onValidate={handleValidation}
                        onChange={handleInputChange}
                    />
                </div>
                <div className="lg:w-1/2 w-full p-5">
                    {/* contactFirstname */}
                    <FormElement
                        value={formFields.prenom_representant_legal}
                        name="prenom_representant_legal"
                        type="text"
                        label={t("store.contact_firstname", { ns: "partner" })}
                        className="mb-5"
                        required={true}
                        disabled={!formEvents.editable}
                        showErrors={showLocalErrors}
                        onValidate={handleValidation}
                        onChange={handleInputChange}
                    />
                </div>

                <div className="lg:w-1/2 w-full p-5">
                    {/* contactPhoneNumber */}
                    <FormElement
                        value={formFields.telephone_1}
                        name="telephone_1"
                        type="phone"
                        label={t("store.contact_phonenumber", { ns: "partner" })}
                        className="mb-5"
                        required={true}
                        disabled={!formEvents.editable}
                        showErrors={showLocalErrors}
                        onValidate={handleValidation}
                        onChange={handleInputChange}
                    />
                </div>

                <div className="lg:w-1/2 w-full p-5">
                    {/* contactMail */}
                    <FormElement
                        value={formFields.mail}
                        name="mail"
                        type="email"
                        label={t("store.contact_mail", { ns: "partner" })}
                        className="mb-5"
                        required={true}
                        disabled={!formEvents.editable}
                        showErrors={showLocalErrors}
                        onValidate={handleValidation}
                        onChange={handleInputChange}
                    />
                </div>
            </div>

            {entityRole === "MANAGEBIKES" && 
                <p>{t("store.update_informations", { ns: "partner" })}</p>
            }
            {!isViewMode && 
                <div className="flex justify-end">
                    {(formFields.manager?.status === 0 && formFields.status !== 2) &&
                        <button
                            type="button"
                            className="c-btn c-btn--secondary px-8"
                            onClick={sendMail}
                        >
                            {t("manager.sendmail", { ns: "partner" }) }
                        </button>
                    }
                </div>
            }
        </section>

    )
}
